import React, { useEffect, useState } from 'react'
import { Global, css } from '@emotion/react'
import {
  BlockingLoader,
  Container,
  ToastContext,
  Carousel,
  CarouselCards,
  Tools,
  HtmlContent,
  ListContainer,
  Css
} from '@pdrj/pmi-iqos-ds-react'
import {
  CommonTranslations,
  parseTranslations,
  DailyPrizesCard,
  PublishedComponent,
  extractErrorMessage,
  getDynamicPath,
  Routes,
  analytics
} from '@pdrj/pmi-iqos-shared-react'
import { getInitialPropsMock } from '@iqosphere-common/utils'
import { api } from '@iqosphere-common/services'
import DailyPrizesTranslations from './daily-prizes.json'

const ItemCard = ({ product, translations }) => {
  const [selectedOptionIndex] = useState(
    product.options ? product.selectedOptionIndex || 0 : null
  )

  const { options } = product

  const { images, badges, shortDescription, disclaimer } =
    options[selectedOptionIndex] || {}

  const seoName = product.options[selectedOptionIndex].seoName
  const dynamicpathValue =
    product.options.length > 0
      ? product.options[selectedOptionIndex].variantId
      : null
  const href = seoName
    ? getDynamicPath(Routes.iqospherePrizes, seoName)
    : getDynamicPath(
        getDynamicPath(Routes.iqospherePrizes, product.productId),
        dynamicpathValue
      )

  return (
    <Container overflowHidden display={Container.displays.flex} fullHeight>
      <DailyPrizesCard
        translations={translations}
        onClickHandler={() => analytics.onDailyPrizesClick(product.name)}
        href={href}
        images={images}
        title={product.name}
        shortDescription={shortDescription}
        testingPrefix="DailyPrizesCard"
        alternativeBadge={badges}
        disclaimerImage={disclaimer}
      />
    </Container>
  )
}

const DailyPrizesComponent = ({
  campaign,
  categoryid,
  translations,
  design,
  gradientBackground,
  header
}) => {
  const [prizeList, setPrizeList] = useState([])
  const [loading, setLoading] = useState(false)

  const popupToast = ToastContext.getContextActions().popupToast
  const campaignTheme = campaign && campaign.class_names

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      try {
        const res = await api.iqospherePrize.getPrizes(categoryid)
        const mappedProductList = res.prizes.map(item => ({
          ...item,
          options: item.options.map(option => ({
            ...option,
            ...(option.badges && {
              badges: option.badges.map(badge => ({
                ...badge,
                font_colour: badge.fontColour,
                background_colour: badge.backgroundColour
              }))
            }),
            color: option.hexColor,
            images: option.images.map(image => ({
              ...image,
              path: image.imagePath
            }))
          }))
        }))

        setPrizeList(mappedProductList)
        setLoading(false)
      } catch (ex) {
        handleError(ex)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const handleError = err => {
    const message = extractErrorMessage(err, translations.tk_error)
    popupToast({
      type: ToastContext.types.error,
      subtitle: message
    })
  }

  const renderCards = () => {
    const cards = !Tools.isDesktop()
      ? prizeList.map((product, i) => {
          return (
            <ItemCard key={i} product={product} translations={translations} />
          )
        })
      : prizeList
          .reduce((all, one, i) => {
            const ch = Math.floor(i / 4)
            all[ch] = [].concat(all[ch] || [], one)
            return all
          }, [])
          .map((items, index) => {
            return (
              <Container
                fullHeight
                display={Container.displays.flex}
                key={index}
                pl={index ? '1' : '0'}
                pr={index === items.length - 1 ? '0' : '1'}>
                <ListContainer columns={4} xBetween="2" fullHeight fullWidth>
                  {items.map((product, idx) => {
                    return (
                      <Container
                        key={idx}
                        display={Container.displays.flex}
                        fullHeight={false}>
                        <ItemCard
                          product={product}
                          translations={translations}
                        />
                      </Container>
                    )
                  })}
                </ListContainer>
              </Container>
            )
          })

    if (!cards || !cards.length) return null
    return !Tools.isDesktop() ? (
      <CarouselCards cards={cards} showIndicators invertIndicatorColor />
    ) : (
      <Carousel
        invertIndicatorColor
        imageSlideSize="100%"
        showArrowsOnHover
        showArrows={true}
        infinite={false}>
        {cards}
      </Carousel>
    )
  }

  const renderTiles = () => {
    return (
      <ListContainer columns={[2, 2, 4]} xBetween="8" yBetween="8" fullWidth>
        {prizeList.map((product, idx) => {
          return (
            <Container
              key={idx}
              display={Container.displays.flex}
              fullHeight={false}>
              <ItemCard product={product} translations={translations} />
            </Container>
          )
        })}
      </ListContainer>
    )
  }
  return (
    <Container
      overflowHidden
      additionalClasses={`${campaignTheme} ${gradientBackground}`}
      pb="4">
      <BlockingLoader loading={loading} />
      <Global
        styles={css`
          .slider-wrapper,
          .carousel-slider {
            margin-left: 0 !important;
            overflow: visible !important;
            width: ${Tools.isDesktop() ? '95%' : '90%'} !important;
          }
        `}
      />
      <Container layout={Container.layouts.full} px={['8', '8', '16']}>
        {design === 'carousel' && (
          <Container py="8">
            <HtmlContent content={header} />
          </Container>
        )}
        <Css s={{ minHeight: '350px' }}>
          {design === 'carousel' ? renderCards() : renderTiles()}
        </Css>
      </Container>
    </Container>
  )
}

const DailyPrizes = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={DailyPrizesComponent}
        {...ComponentDefaultProps}
        {...props}
        fullWidth
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...DailyPrizesTranslations,
  ...CommonTranslations
}

const defaultTranslations = {
  ...parseTranslations(ComponentTranslations, true)
}

const ComponentDefaultProps = {
  componentName: 'DailyPrizes',
  translations: defaultTranslations,
  defaultTranslations,
  translationsConfig: ComponentTranslations
}

DailyPrizes.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const campaign =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('dailyPrizes')
          : (req.body.extra && req.body.extra.campaign) || ''
      const categoryid =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('campaignId')
          : (req.body.extra && req.body.extra.categoryid) || ''
      const design =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('design')
          : (req.body.extra && req.body.extra.design) || ''
      const header =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('dailyPrizesHeader')
          : (req.body.extra && req.body.extra.header) || ''
      const gradientBackground =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('dailyPrizesBackground')
          : (req.body.extra && req.body.extra.classes) || ''
      return {
        campaign,
        categoryid,
        design,
        header,
        gradientBackground
      }
    }
  })
}

export default DailyPrizes
